import SerializeSimplified from "@/core/misc/decorators/SerializeSimplified";
import Title_ru_RU         from "@/core/misc/decorators/Title_ru_RU";
import TYPES               from '@/core/services/Changelogs/types';
import Core                from "@/core/Core";
import BaseEntity          from '@/core/misc/BaseEntity';
import Partner             from '@/domains/Partners/Partner';
import Company             from '@/domains/Companies/Company';
import Person              from '@/domains/Persons/Person';
import OrderCategory       from "@/domains/Orders/OrderCategory";
import type OrderPosition from "@/domains/Orders/OrderPosition";
import type AbstractDocumentPosition from "@/domains/Abstract/AbstractDocumentPosition";

/**
 * Абстрактный документ (отгрузка, сборка)
 */
abstract class AbstractDocument<TPosition = any> extends BaseEntity {

  /** Базируется на предложении */
  public isOfferBased: boolean;
  /** Иностранный продавец */
  public isForeign: boolean;
  /** Содержит образцы */
  public isSamples: boolean;
  @Title_ru_RU('Отметка завершённости', TYPES.BOOLEAN)
  public isFinished: boolean;

  /** НДС включен в цены */
  @Title_ru_RU('"НДС включительно"', TYPES.BOOLEAN)
  public isVatIncluded: boolean;

  /** Категория заказа */
  @SerializeSimplified
  public orderCategory: OrderCategory;

  /** Продавец */
  @SerializeSimplified
  public seller: Partner;
  /** Юр. лицо продавца */
  @SerializeSimplified
  public sellerCompany: Company;

  /** Покупатель */
  @SerializeSimplified
  public buyer: Partner | null = null;
  /** Юр. лицо покупателя */
  @SerializeSimplified
  public buyerCompany: Company;

  /** Ответственный от покупателя */
  @SerializeSimplified
  public responsibleBuyer: Person;

  /** Позиции документа */
  @Title_ru_RU('Позиции', TYPES.STRING)
  public positions: TPosition[] = [];

  static isAbstractDocument(entity: unknown): entity is AbstractDocument {
    return entity instanceof AbstractDocument;
  }

  static fromServer(data: any, result: AbstractDocument): AbstractDocument {
    super.fromServer(data, result);

    result.orderCategory = Core.utils.hydrate(data.orderCategory, OrderCategory.fromServer)!;

    result.seller = Core.utils.hydrate(data.seller, Partner.fromServer)!;
    result.sellerCompany = Core.utils.hydrate(data.sellerCompany, Company.fromServer)!;
    result.buyer = Core.utils.hydrate(data.buyer, Partner.fromServer)!;
    result.buyerCompany = Core.utils.hydrate(data.buyerCompany, Company.fromServer)!;

    result.responsibleBuyer = Core.utils.hydrate(data.responsibleBuyer, Person.fromServer)!;

    return result;
  }

  static fromOrderPosition(orderPosition: OrderPosition, result: AbstractDocument): AbstractDocument {

    result.isOfferBased  = orderPosition.isOfferBased;
    result.isSamples    = orderPosition.isSamples;

    result.orderCategory = orderPosition.orderCategory;

    result.seller       = orderPosition.seller;
    result.sellerCompany = orderPosition.sellerCompany;

    if (result.sellerCompany?.country?.code !== undefined && result.sellerCompany?.country?.code !== null) {
      result.isForeign = result.sellerCompany.country.code !== Core.config.localCountry.code;
    }

    result.buyer       = orderPosition.buyer;
    result.buyerCompany = orderPosition.buyerCompany;

    result.isVatIncluded = orderPosition.isVatIncludedSeller;

    return result;
  }

  static fromNew(result: AbstractDocument): AbstractDocument {

    if (Core.currentPartner?.isBuyer) {
      result.buyer = Core.currentPartner;
      result.responsibleBuyer = Core.currentUser!;

      /** Если это сибирское здоровье */
      if (result.buyer.isLSZ) {
        result.buyerCompany = result.buyer.companies.find(_company => _company.id === 66) || result.buyer.companies[0];
      }
      /** Если это наманган */
      else if (result.buyer.isNamangan) {
        result.buyerCompany = result.buyer.companies.find(_company => _company.id === 495) || result.buyer.companies[0];
      }
      else if (result.buyer.companies.length === 1) {
        result.buyerCompany = result.buyer.companies[0];
      }
    }
    else if (Core.currentPartner?.isSeller) {
      result.seller = Core.currentPartner;

      if (result.seller.companies.length === 1) {
        result.sellerCompany = result.seller.companies[0];
      }
    }

    return result;
  }

  get sellerString(): string | null {
    if (!this.sellerCompany) {
      return 'Не выбран';
    }
    if (this.sellerCompany.name) {
      return `${this.sellerCompany.name} (${this.seller.name})`;
    }
    return this.seller.name;
  }

  get buyerString(): string | null {
    if (this.buyerCompany?.name) {
      return `${this.buyerCompany.name} ${this.buyer?.name}`;
    }
    if (this.buyer?.name) {
      return this.buyer.name;
    }
    return '--';
  }

  /** Документ из категории «Полиграфия». */
  get isPrinting(): boolean {
    return this.orderCategory?.id === 'printing';
  }

  /** Документ из категории «Сырьё и упаковка». */
  get isRawMaterialsAndPackage(): boolean {
    return this.orderCategory?.id === 'rawMaterialsAndPackage';
  }

  addPosition(newPosition: TPosition): void {
    this.validatePosition(newPosition);

    this.positions.push(newPosition);
  }

  /**
   * Проверяет, что позиция совместима с документом: собирать в один документ
   * позиции разных сделок нельзя.
   * Потомки могут расширить набор проверок через super.validatePosition()
   *
   * Позиции всех документов наследуют AbstractDocumentPosition, поэтому
   * ссылка на позицию заказа у них есть всегда.
   */
  validatePosition(position: TPosition): void {
    const orderPosition = (position as AbstractDocumentPosition).orderPosition;

    if (this.isOfferBased !== undefined && this.isOfferBased !== orderPosition.isOfferBased) {
      throw Error('Не совпадает по предложению/нет у позиции и документа');
    }
    if (this.isForeign !== undefined && this.isForeign !== orderPosition.isForeign) {
      throw Error('Не совпадает статус ВЭД у позиции и документа');
    }
    if (this.orderCategory?.id && this.orderCategory.id !== orderPosition.orderCategory?.id) {
      throw Error('Не совпадают категории заказов у позиции и документа');
    }
    if (this.seller?.id && this.seller.id !== orderPosition.seller?.id) {
      throw Error('Не совпадает партнер продавца у позиции и документа');
    }
    if (this.buyer?.id && this.buyer.id !== orderPosition.buyer?.id) {
      throw Error('Не совпадает партнер покупателя у позиции и документа');
    }
    if (this.sellerCompany?.id && this.sellerCompany.id !== orderPosition.sellerCompany?.id) {
      throw Error('Не совпадает компания продавца у позиции и документа');
    }
    if (this.buyerCompany?.id && this.buyerCompany.id !== orderPosition.buyerCompany?.id) {
      throw Error('Не совпадает компания покупателя у позиции и документа');
    }
  }
}

export default AbstractDocument;
